import {
  useMutation,
  useQueryClient,
  UseMutateFunction,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';

import type { Entity, HelperResult } from '@openclassrooms/sdk-web';

import { getDependentEntities } from './dependentEntities';

type CreateFunction<CreateRequestParams, CreateRequestBody, CreateResponseBody> = (
  requestBody: CreateRequestBody,
  params?: CreateRequestParams
) => HelperResult<CreateResponseBody>;

type MutationOptions<CreateResponseBody, CreateRequestBody> = Omit<
  UseMutationOptions<CreateResponseBody, unknown, CreateRequestBody>,
  'mutationFn'
>;

type UseCreateReturnType<CreateResponseBody, CreateRequestBody> = [
  UseMutateFunction<CreateResponseBody, unknown, CreateRequestBody>,
  Omit<UseMutationResult<CreateResponseBody, unknown, CreateRequestBody>, 'mutate'>
];

export default function getUseCreate<
  CreateRequestParams,
  EntityType extends Entity = Entity,
  CreateRequestBody extends Partial<Entity> = Partial<EntityType>,
  CreateResponseBody extends Partial<Entity> = Partial<EntityType>
>(
  create: CreateFunction<CreateRequestParams, CreateRequestBody, CreateResponseBody>,
  queryKey: string,
  queryKeyList: string,
  sharedCacheEntities: string[] = []
) {
  return (
    params?: CreateRequestParams,
    mutationOptions: MutationOptions<CreateResponseBody, CreateRequestBody> = {}
  ): UseCreateReturnType<CreateResponseBody, CreateRequestBody> => {
    const queryClient = useQueryClient();

    const { mutate, ...mutationResult } = useMutation({
      ...mutationOptions,
      mutationFn: async (newEntity) => {
        const { body } = await create(newEntity, params);
        return body;
      },
      onSuccess: (newEntity, variables, context) => {
        if (newEntity?.id) {
          queryClient.setQueryData([queryKey, newEntity.id], newEntity);
        }
        queryClient.invalidateQueries([queryKeyList]);

        new Set([...sharedCacheEntities, ...getDependentEntities(queryKey)]).forEach(
          (sharedCacheEntity) => {
            queryClient.invalidateQueries([sharedCacheEntity]);
          }
        );

        if (mutationOptions?.onSuccess) {
          mutationOptions.onSuccess(newEntity, variables, context);
        }
      },
    });

    return [mutate, mutationResult];
  };
}
